import { useState, useRef, useEffect } from 'react'
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Avatar,
  Collapse,
  Divider,
} from '@mui/material'
import {
  Send as SendIcon,
  Reply as ReplyIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material'
import { Client, Comment, commentsApi } from '../../services/api'
import { useNotification } from '../../hooks/useNotification'
import { useWebSocket } from '../../hooks/useWebSocket'

interface CommentsTabProps {
  client: Client
  onUpdate?: (client: Partial<Client>) => void
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
}

function getInitials(email: string): string {
  const parts = email.split('@')[0].split('.')
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase()
  }
  return email.substring(0, 2).toUpperCase()
}

function formatName(email: string): string {
  const parts = email.split('@')[0].split('.')
  return parts.map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(' ')
}

function stringToColor(str: string): string {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  }
  const colors = ['#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#c2185b', '#0097a7', '#5d4037']
  return colors[Math.abs(hash) % colors.length]
}

interface CommentItemProps {
  comment: Comment
  replies: Comment[]
  onReply: (parentId: string) => void
  replyingTo: string | null
  replyContent: string
  setReplyContent: (content: string) => void
  onSubmitReply: () => void
  submitting: boolean
}

function CommentItem({
  comment,
  replies,
  onReply,
  replyingTo,
  replyContent,
  setReplyContent,
  onSubmitReply,
  submitting,
}: CommentItemProps) {
  const [showReplies, setShowReplies] = useState(true)
  const isReplying = replyingTo === comment.id

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Avatar
          sx={{
            width: 36,
            height: 36,
            fontSize: 14,
            bgcolor: stringToColor(comment.createdBy),
          }}
        >
          {getInitials(comment.createdBy)}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {formatName(comment.createdBy)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatRelativeTime(comment.createdAt)}
            </Typography>
            {comment.editedAt && (
              <Typography variant="caption" color="text.secondary">
                (edited)
              </Typography>
            )}
          </Box>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
            {comment.content}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              size="small"
              startIcon={<ReplyIcon sx={{ fontSize: 16 }} />}
              onClick={() => onReply(comment.id)}
              sx={{ textTransform: 'none', color: 'text.secondary', minWidth: 'auto', p: 0.5 }}
            >
              Reply
            </Button>
            {replies.length > 0 && (
              <Button
                size="small"
                onClick={() => setShowReplies(!showReplies)}
                endIcon={showReplies ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ textTransform: 'none', color: 'text.secondary', minWidth: 'auto', p: 0.5 }}
              >
                {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
              </Button>
            )}
          </Box>

          {/* Reply input */}
          <Collapse in={isReplying}>
            <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Write a reply..."
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                multiline
                maxRows={4}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    onSubmitReply()
                  }
                }}
              />
              <Button
                variant="contained"
                size="small"
                disabled={submitting || !replyContent.trim()}
                onClick={onSubmitReply}
                sx={{ minWidth: 'auto', px: 2 }}
              >
                {submitting ? <CircularProgress size={20} /> : <SendIcon sx={{ fontSize: 18 }} />}
              </Button>
            </Box>
          </Collapse>

          {/* Replies */}
          <Collapse in={showReplies && replies.length > 0}>
            <Box sx={{ mt: 2, pl: 2, borderLeft: '2px solid', borderColor: 'divider' }}>
              {replies.map((reply) => (
                <Box key={reply.id} sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      fontSize: 12,
                      bgcolor: stringToColor(reply.createdBy),
                    }}
                  >
                    {getInitials(reply.createdBy)}
                  </Avatar>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 13 }}>
                        {formatName(reply.createdBy)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatRelativeTime(reply.createdAt)}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>
                      {reply.content}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Collapse>
        </Box>
      </Box>
    </Box>
  )
}

export default function CommentsTab({ client, onUpdate }: CommentsTabProps) {
  const { showSuccess, showError } = useNotification()
  const [comments, setComments] = useState<Comment[]>(client.comments || [])
  const [newComment, setNewComment] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [replyingTo, setReplyingTo] = useState<string | null>(null)
  const [replyContent, setReplyContent] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  // Update comments when client changes
  useEffect(() => {
    setComments(client.comments || [])
  }, [client.comments])

  // WebSocket handler for real-time comment updates
  useWebSocket({
    comment_added: (message) => {
      const data = message.data as { entityId: string; comment: Comment }
      if (data.entityId === client.id) {
        setComments((prev) => {
          if (prev.some((c) => c.id === data.comment.id)) return prev
          return [...prev, data.comment]
        })
      }
    },
  })

  const handleSubmitComment = async () => {
    if (!newComment.trim()) return

    try {
      setSubmitting(true)
      const result = await commentsApi.add(client.id, newComment.trim())
      setComments((prev) => [...prev, result.comment])
      setNewComment('')
      showSuccess('Comment added')
      onUpdate?.({ comments: [...comments, result.comment] })
    } catch (err) {
      console.error('Error adding comment:', err)
      showError(err instanceof Error ? err.message : 'Failed to add comment')
    } finally {
      setSubmitting(false)
    }
  }

  const handleSubmitReply = async () => {
    if (!replyContent.trim() || !replyingTo) return

    try {
      setSubmitting(true)
      const result = await commentsApi.add(client.id, replyContent.trim(), { parentId: replyingTo })
      setComments((prev) => [...prev, result.comment])
      setReplyContent('')
      setReplyingTo(null)
      showSuccess('Reply added')
      onUpdate?.({ comments: [...comments, result.comment] })
    } catch (err) {
      console.error('Error adding reply:', err)
      showError(err instanceof Error ? err.message : 'Failed to add reply')
    } finally {
      setSubmitting(false)
    }
  }

  const handleReply = (parentId: string) => {
    if (replyingTo === parentId) {
      setReplyingTo(null)
      setReplyContent('')
    } else {
      setReplyingTo(parentId)
      setReplyContent('')
    }
  }

  // Organize comments into threads
  const topLevelComments = comments.filter((c) => !c.parentId)
  const repliesByParent = comments.reduce(
    (acc, comment) => {
      if (comment.parentId) {
        if (!acc[comment.parentId]) acc[comment.parentId] = []
        acc[comment.parentId].push(comment)
      }
      return acc
    },
    {} as Record<string, Comment[]>
  )

  // Sort top-level by newest first, replies by oldest first
  topLevelComments.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
  Object.values(repliesByParent).forEach((replies) => {
    replies.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
  })

  return (
    <Box>
      {/* New comment input */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
          Add a comment
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <TextField
            inputRef={inputRef}
            fullWidth
            placeholder="Write a comment... (Shift+Enter for new line)"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            multiline
            maxRows={6}
            size="small"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                handleSubmitComment()
              }
            }}
          />
          <Button
            variant="contained"
            disabled={submitting || !newComment.trim()}
            onClick={handleSubmitComment}
            sx={{ minWidth: 'auto', px: 2, alignSelf: 'flex-end' }}
          >
            {submitting ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
          </Button>
        </Box>
      </Paper>

      {/* Comments list */}
      {comments.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            No comments yet. Start a conversation about this client.
          </Typography>
        </Box>
      ) : (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {comments.length} {comments.length === 1 ? 'comment' : 'comments'}
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          {topLevelComments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              replies={repliesByParent[comment.id] || []}
              onReply={handleReply}
              replyingTo={replyingTo}
              replyContent={replyContent}
              setReplyContent={setReplyContent}
              onSubmitReply={handleSubmitReply}
              submitting={submitting}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}
